import {
  Action,
  PathsV1VehicleValuationSearchPostParametersQueryExtend,
  PathsV1VehicleValuationSearchPostParametersQueryOrderByDirection,
  PathsV1VehicleValuationSearchPostParametersQueryOrderByField,
} from "@/api-schema-enums";
import { useAbility } from "@/casl-provider";
import { UNLIMITED_PAGE_SIZE } from "@/common/api/_defaults";
import { useVehicleValuationSearchQuery } from "@/common/api/vehicle-valuation/use-vehicle-valuation-search.query";
import { openInNewTab } from "@/common/helpers/open-in-new-tab";
import { automaticCellRenderer } from "@/components/data-table/_helpers/cell-renderer";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CommandButton } from "@/components/command-palette/command-button";
import { ButtonColorVariant, ButtonSize, ButtonVariant } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyValue } from "@/components/ui/empty-value";
import { VehicleValuationSnapshotAction } from "@/components/vehicle-valuation/vehicle-valuation-snapshot-action";
import { DetailInfoRow } from "@/views/vehicle-valuation-detail/_components/detail-info-row";
import { AlertTriangle, DownloadIcon, EyeIcon } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  renderVehicleValuationMileageValue,
  VehicleValuationHistoryCard,
} from "./_components/vehicle-valuation-history-card";
import {
  buildNumberOfHoldersMismatchMap,
  buildValuationHistoryEntries,
  formatIntegerText,
  hasCalculatedVehicleValuationResult,
  sortVehicleValuationsByCreatedAtDescending,
  sortVehicleValuationsByMileageAscending,
  type VehicleValuationHistorySummary,
} from "./_helpers/vehicle-valuation-history.helpers";

const VEHICLE_VALUATION_HISTORY_EXTEND: PathsV1VehicleValuationSearchPostParametersQueryExtend[] = [
  PathsV1VehicleValuationSearchPostParametersQueryExtend.Vehicle,
  PathsV1VehicleValuationSearchPostParametersQueryExtend.Requester,
  PathsV1VehicleValuationSearchPostParametersQueryExtend.Vehicle_ModelGeneration,
  PathsV1VehicleValuationSearchPostParametersQueryExtend.Vehicle_ModelGeneration_ModelFamily,
  PathsV1VehicleValuationSearchPostParametersQueryExtend.Vehicle_ModelGeneration_ModelFamily_Brand,
  PathsV1VehicleValuationSearchPostParametersQueryExtend.Vehicle_OwningBranch,
  PathsV1VehicleValuationSearchPostParametersQueryExtend.Vehicle_CurrentBranch,
  PathsV1VehicleValuationSearchPostParametersQueryExtend.ConfirmedBy,
];

const VEHICLE_VALUATION_HISTORY_ORDER_BY = [
  {
    direction: PathsV1VehicleValuationSearchPostParametersQueryOrderByDirection.Desc,
    field: PathsV1VehicleValuationSearchPostParametersQueryOrderByField.CreatedAt,
  },
] as const;

export function ValuationHistoryTab({ uuid }: { uuid: string }) {
  const { t } = useTranslation(["masterdata", "common", "fab"]);
  const ability = useAbility();
  const canManageVehicleValuation = ability.can(Action.Manage, "VehicleValuation");
  const valuationSearchOptions = useMemo(
    () => ({
      parameters: {
        page: 1,
        pageSize: UNLIMITED_PAGE_SIZE,
        extend: [...VEHICLE_VALUATION_HISTORY_EXTEND],
        orderBy: [...VEHICLE_VALUATION_HISTORY_ORDER_BY],
      },
      body: {
        vehicle: [uuid],
      },
    }),
    [uuid],
  );
  const valuationHistoryQuery = useVehicleValuationSearchQuery({
    options: valuationSearchOptions,
    enabled: Boolean(uuid),
  });
  const valuationHistoryData = useMemo(
    () => valuationHistoryQuery.data?.data ?? [],
    [valuationHistoryQuery.data?.data],
  );
  const valuationsOrderedByCreatedAt = useMemo(
    () => sortVehicleValuationsByCreatedAtDescending(valuationHistoryData),
    [valuationHistoryData],
  );
  const valuationsOrderedByMileage = useMemo(
    () => sortVehicleValuationsByMileageAscending(valuationHistoryData),
    [valuationHistoryData],
  );
  const valuationHistorySummary = useMemo<VehicleValuationHistorySummary>(() => {
    const mismatchByValuationUuid = buildNumberOfHoldersMismatchMap(valuationsOrderedByMileage);
    const historyEntries = buildValuationHistoryEntries(valuationsOrderedByCreatedAt, mismatchByValuationUuid);

    return {
      historyEntries,
      mismatchEntries: historyEntries.filter((entry) => entry.mismatch),
    };
  }, [valuationsOrderedByCreatedAt, valuationsOrderedByMileage]);
  const { historyEntries, mismatchEntries } = valuationHistorySummary;
  const totalValuationCount = valuationHistoryQuery.data?.meta.total ?? historyEntries.length;
  const latestValuation = valuationsOrderedByCreatedAt[0];
  const successfulValuations = valuationsOrderedByCreatedAt.filter((valuation) =>
    hasCalculatedVehicleValuationResult(valuation),
  );
  const latestSuccessfulValuation = successfulValuations[0];

  return (
    <div className="space-y-6">
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <Card>
          <CardHeader>
            <CardTitle>{t("fab:VehicleValuationOverview")}</CardTitle>
            <CardDescription>{t("fab:VehicleValuationOverviewDescription")}</CardDescription>
          </CardHeader>

          <CardContent className="grid gap-4 sm:grid-cols-2">
            <DetailInfoRow
              label={t("fab:TotalVehicleValuations")}
              value={
                valuationHistoryQuery.isPending ? (
                  t("common:Loading")
                ) : valuationHistoryQuery.isError ? (
                  <EmptyValue />
                ) : (
                  totalValuationCount
                )
              }
            />
            <DetailInfoRow
              label={t("fab:SuccessfulVehicleValuations")}
              value={
                valuationHistoryQuery.isPending ? (
                  t("common:Loading")
                ) : valuationHistoryQuery.isError ? (
                  <EmptyValue />
                ) : (
                  successfulValuations.length
                )
              }
            />
            <DetailInfoRow
              label={t("fab:NumberOfHoldersMismatchCount")}
              value={
                valuationHistoryQuery.isPending ? (
                  t("common:Loading")
                ) : valuationHistoryQuery.isError ? (
                  <EmptyValue />
                ) : (
                  mismatchEntries.length
                )
              }
            />
            <DetailInfoRow
              label={t("fab:LatestValuationMileage")}
              value={
                valuationHistoryQuery.isPending ? (
                  t("common:Loading")
                ) : valuationHistoryQuery.isError ? (
                  <EmptyValue />
                ) : (
                  renderVehicleValuationMileageValue(latestValuation)
                )
              }
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("fab:LatestSuccessfulVehicleValuation")}</CardTitle>
            <CardDescription>{t("fab:LatestSuccessfulVehicleValuationDescription")}</CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {valuationHistoryQuery.isPending ? (
              <p className="text-muted-foreground text-sm">{t("common:Loading")}</p>
            ) : valuationHistoryQuery.isError ? (
              <p className="text-sm">
                <EmptyValue />
              </p>
            ) : latestSuccessfulValuation ? (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <DetailInfoRow
                    label={t("fab:TradeGross")}
                    value={automaticCellRenderer(latestSuccessfulValuation.tradeGross, {
                      numberFormat: "currency",
                      numberDecimals: 2,
                    })}
                  />
                  <DetailInfoRow
                    label={t("fab:SalesGross")}
                    value={automaticCellRenderer(latestSuccessfulValuation.salesGross, {
                      numberFormat: "currency",
                      numberDecimals: 2,
                    })}
                  />
                  <DetailInfoRow
                    label={t("fab:MarginGross")}
                    value={automaticCellRenderer(latestSuccessfulValuation.marginGross, {
                      numberFormat: "currency",
                      numberDecimals: 2,
                    })}
                  />
                  <DetailInfoRow
                    label={t("fab:MarginPercentage")}
                    value={automaticCellRenderer(latestSuccessfulValuation.marginPercentage, {
                      numberFormat: "percentage",
                      numberDecimals: 2,
                    })}
                  />
                </div>

                <div className="flex flex-wrap gap-2">
                  <CommandButton
                    commandId="vehicle-latest-valuation-view"
                    label={t("fab:ViewVehicleValuation")}
                    icon={<EyeIcon className="h-4 w-4" />}
                    to={`~/fab/list/${latestSuccessfulValuation.uuid}`}
                    size={ButtonSize.Sm}
                    variant={ButtonVariant.Outlined}
                    colorVariant={ButtonColorVariant.Secondary}
                  />

                  {latestSuccessfulValuation.reportUrl ? (
                    <CommandButton
                      commandId="vehicle-latest-valuation-open-report"
                      label={t("fab:OpenValuationReport")}
                      icon={<DownloadIcon className="h-4 w-4" />}
                      size={ButtonSize.Sm}
                      variant={ButtonVariant.Outlined}
                      colorVariant={ButtonColorVariant.Secondary}
                      onClick={() => openInNewTab(latestSuccessfulValuation.reportUrl)}
                    />
                  ) : null}

                  <VehicleValuationSnapshotAction
                    valuationUuid={latestSuccessfulValuation.uuid}
                    size={ButtonSize.Sm}
                    showInCommandPalette
                  />
                </div>
              </>
            ) : (
              <p className="text-muted-foreground text-sm">{t("fab:NoSuccessfulVehicleValuationAvailable")}</p>
            )}
          </CardContent>
        </Card>
      </div>

      <section className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold">{t("fab:VehicleValuationHistory")}</h2>
          <p className="text-muted-foreground text-sm">{t("fab:VehicleValuationHistoryDescription")}</p>
        </div>

        {valuationHistoryQuery.isError ? (
          <Alert variant="error">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t("fab:VehicleValuationHistory")}</AlertTitle>
            <AlertDescription>{t("fab:VehicleValuationHistoryLoadError")}</AlertDescription>
          </Alert>
        ) : null}

        {!valuationHistoryQuery.isPending && !valuationHistoryQuery.isError && historyEntries.length === 0 ? (
          <Card>
            <CardContent className="py-6">
              <p className="text-muted-foreground text-sm">{t("fab:NoVehicleValuationHistoryForVehicle")}</p>
            </CardContent>
          </Card>
        ) : null}

        {mismatchEntries.length > 0 ? (
          <Alert variant="warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t("fab:VehicleValuationHistoryMismatchTitle")}</AlertTitle>
            <AlertDescription>
              <p>{t("fab:VehicleValuationHistoryMismatchDescription")}</p>
              <ul className="mt-2 list-disc pl-5">
                {mismatchEntries.map((entry) => (
                  <li key={entry.valuation.uuid}>
                    {t("fab:VehicleValuationHistoryMismatchItem", {
                      currentMileage: formatIntegerText(entry.mismatch?.current.mileageUsed),
                      currentOwners: formatIntegerText(entry.mismatch?.current.numberOfHoldersUsed),
                      referenceMileage: formatIntegerText(entry.mismatch?.reference.mileageUsed),
                      referenceOwners: formatIntegerText(entry.mismatch?.reference.numberOfHoldersUsed),
                    })}
                  </li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        ) : null}

        {valuationHistoryQuery.isPending ? (
          <Card>
            <CardContent className="py-6">
              <p className="text-muted-foreground text-sm">{t("common:Loading")}</p>
            </CardContent>
          </Card>
        ) : null}

        {!valuationHistoryQuery.isPending && !valuationHistoryQuery.isError && historyEntries.length > 0 ? (
          <div className="space-y-4">
            {historyEntries.map((entry) => (
              <VehicleValuationHistoryCard
                key={entry.valuation.uuid}
                entry={entry}
                canManageVehicleValuation={canManageVehicleValuation}
              />
            ))}
          </div>
        ) : null}
      </section>
    </div>
  );
}
